/**
 * One stage column on the pipeline board.
 *
 * The column is a drop target and a list at once: `useDroppable` gives it the
 * drop behaviour, and the `<ul>` inside gives assistive technology the count
 * and structure that a bare div would not.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.1
 */
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';

import type { StartupSummary } from '@nexus/contracts';

import type { Stage } from '../../../components/domain/stages';
import { STAGE_COLOR, STAGE_DOT } from '../../../components/domain/stages';
import { cn } from '../../../lib/cn';
import { formatNumber } from '../../../lib/format';

import { BoardCard } from './BoardCard';

export interface BoardColumnProps {
  stage: Stage;
  startups: readonly StartupSummary[];
  onOpen: (id: string) => void;
  canEdit: boolean;
  /** True on narrow screens, where one column fills the width. */
  isSingleColumn?: boolean;
}

export function BoardColumn({
  stage,
  startups,
  onOpen,
  canEdit,
  isSingleColumn = false,
}: BoardColumnProps): React.JSX.Element {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({ id: stage });

  const label = t('stage.' + stage);

  return (
    <section
      ref={setNodeRef}
      /* `role="group"`, not the implicit `region` a named `section` becomes.
         Nine landmarks named after stages would bury the page's real ones in a
         screen reader's landmark list; a column is a labelled group within the
         board, not a top-level region of the page.

         (This originally also worked around both layouts being in the DOM at
         once, which made every stage a duplicated landmark. Only one layout is
         rendered now — see `StartupBoard` — but the reasoning above stands on
         its own.)

         Labelled with the stage AND the count: a screen reader user moving a
         card needs to know what is in the column they are about to drop into,
         not only its name. */
      role="group"
      aria-label={t('board.columnLabel', { stage: label, count: startups.length })}
      className={cn(
        /* A white elevated surface on the off-white page ground, per the
           reference prototype. Built off-white with no elevation, a column
           reads as a gap in the page rather than as a container. */
        'flex flex-col rounded-md bg-white shadow-sm',
        /* `self-stretch` inside the board's flex row makes every column the
           same height regardless of how many cards it holds, and the list
           below grows to absorb that height. Sized to content, a column with
           one card is a 90px stub whose droppable area is the card itself —
           so the board is hardest to drop into exactly when it is emptiest. */
        'self-stretch',
        /* `flex-1` with a floor, not a fixed width: nine columns then share the
           board's width and it does not scroll, which is what keeps every stage
           reachable by keyboard. */
        isSingleColumn ? 'w-full' : 'min-w-[180px] flex-1',
        /* A visible drop indication, not only a cursor change. `isOver` is
           dnd-kit drag state, set from element geometry jsdom does not
           compute, so it is unreachable here and verified in a browser. */
        /* v8 ignore next */
        isOver ? 'ring-blue ring-2' : '',
      )}
    >
      {/* A div, not a `header`: outside a sectioning element a `header` is a
          BANNER landmark, so every column would claim to be the page banner. */}
      {/* A 2px underline in the stage's own colour — the prototype's way of
          colour-coding a column without tinting its whole background, which
          would fight the cards inside it. */}
      <div
        className="flex items-center gap-2 border-b-2 px-3.5 py-3"
        style={{ borderBottomColor: STAGE_COLOR[stage] }}
      >
        {/* Decorative: the heading text beside it names the stage. */}
        <span
          aria-hidden="true"
          className={cn('size-[7px] rounded-full', STAGE_DOT[stage])}
        />
        <h2 className="text-navy-text flex-1 text-xs font-extrabold tracking-[0.1em] uppercase">
          {label}
        </h2>
        <span className="text-gray-400 text-xs tabular-nums">
          {formatNumber(startups.length)}
        </span>
      </div>

      <SortableContext
        items={startups.map((startup) => startup.id)}
        strategy={verticalListSortingStrategy}
      >
        {/* `flex-1` so the list, not the section, absorbs the stretched
            height: the droppable node is the section, and empty space below
            the last card has to be part of it for a drop there to land. */}
        <ul className="p-s2 gap-s2 flex min-h-14 flex-1 flex-col">
          {startups.length === 0 ? (
            <li className="text-body py-3.5 text-center text-xs font-light">
              {t('board.empty')}
            </li>
          ) : (
            startups.map((startup) => (
              <BoardCard
                key={startup.id}
                startup={startup}
                onOpen={onOpen}
                canEdit={canEdit}
              />
            ))
          )}
        </ul>
      </SortableContext>
    </section>
  );
}
